import { getAPIURL, handleResponseError } from "./api";

const normalizePortalJobTypesList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  return [];
};

/**
 * Список видов работ портала.
 * GET /portal_job_types/all
 * @param {{
 *   is_active?: boolean,
 *   type_of_fin_job_type?: string,
 *   role_id?: number,
 *   department_id?: number,
 * }} [params]
 */
export const getAllPortalJobTypes = async (params = {}) => {
  const searchParams = new URLSearchParams();

  if (params.is_active !== undefined && params.is_active !== null) {
    searchParams.set("is_active", String(params.is_active));
  }
  if (params.type_of_fin_job_type) {
    searchParams.set("type_of_fin_job_type", params.type_of_fin_job_type);
  }
  if (params.role_id != null) {
    searchParams.set("role_id", String(params.role_id));
  }
  if (params.department_id != null) {
    searchParams.set("department_id", String(params.department_id));
  }

  const query = searchParams.toString();
  const response = await fetch(
    `${getAPIURL()}portal_job_types/all${query ? `?${query}` : ""}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    await handleResponseError(response, "Ошибка при загрузке видов работ");
  }

  const data = await response.json();
  return normalizePortalJobTypesList(data);
};

/**
 * Обновление вида работ портала.
 * PATCH /portal_job_types/{job_type_id}
 * @param {number} jobTypeId
 * @param {{
 *   name?: string,
 *   is_active?: boolean,
 *   fin_job_type_id?: number,
 *   type_of_fin_job_type?: string,
 *   department_id?: number,
 *   role_id?: number,
 *   project_section_id?: number | null,
 * }} data
 */
export const updatePortalJobType = async (jobTypeId, data) => {
  const response = await fetch(
    `${getAPIURL()}portal_job_types/${jobTypeId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    await handleResponseError(response, "Ошибка при обновлении вида работ");
  }

  return response.json();
};

/**
 * Создание вида работ портала.
 * POST /portal_job_types/add
 * @param {{
 *   role_ids: number[],
 *   for_all_roles: boolean,
 *   name: string,
 *   is_active?: boolean,
 *   fin_job_type_id: number,
 *   type_of_fin_job_type: string,
 *   department_id?: number | null,
 * }} data
 */
export const addPortalJobType = async (data) => {
  const body = {
    role_ids: data.role_ids || [],
    for_all_roles: Boolean(data.for_all_roles),
    name: data.name,
    is_active: Boolean(data.is_active),
    fin_job_type_id: data.fin_job_type_id,
    type_of_fin_job_type: data.type_of_fin_job_type,
    department_id:
      data.department_id != null && data.department_id !== ""
        ? Number(data.department_id)
        : null,
  };

  const response = await fetch(`${getAPIURL()}portal_job_types/add`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await handleResponseError(response, "Ошибка при создании вида работ");
  }

  return response.json();
};

/**
 * Привязка существующих видов работ к роли.
 * POST /portal_job_types/assign_to_role
 * Новые виды работ не создаются, уже существующие связи пропускаются.
 *
 * Скопировать все связи с другой роли:
 *   { role_id, source_role_id }
 * Скопировать выбранные связи с другой роли:
 *   { role_id, source_role_id, portal_job_type_ids }
 * Привязать выбранные виды работ напрямую:
 *   { role_id, portal_job_type_ids }
 *
 * @param {{
 *   role_id: number,
 *   source_role_id?: number,
 *   portal_job_type_ids?: number[],
 * }} data
 */
export const assignPortalJobTypesToRole = async (data) => {
  const body = {
    role_id: Number(data.role_id),
  };

  if (data.source_role_id != null && data.source_role_id !== "") {
    body.source_role_id = Number(data.source_role_id);
  }

  if (Array.isArray(data.portal_job_type_ids) && data.portal_job_type_ids.length > 0) {
    body.portal_job_type_ids = data.portal_job_type_ids.map((id) => Number(id));
  }

  const response = await fetch(
    `${getAPIURL()}portal_job_types/assign_to_role`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    await handleResponseError(
      response,
      "Ошибка при привязке видов работ к роли",
    );
  }

  const text = await response.text();
  if (!text) return "";

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};
